import { useState, useEffect } from 'react';
import { Page } from '@/types/page';

interface UsePagePreloadingProps {
  leftPage: Page | null;
  rightPage: Page | null;
  fullscreenPage: Page | null;
  nextLeftPage: Page | null;
  nextRightPage: Page | null;
  nextFullscreenPage: Page | null;
}

interface UsePagePreloadingResult {
  visibleLeftPage: Page | null;
  visibleRightPage: Page | null;
  visibleFullscreenPage: Page | null;
  isLoading: boolean;
}

export const usePagePreloading = ({
  leftPage,
  rightPage,
  fullscreenPage,
  nextLeftPage,
  nextRightPage,
  nextFullscreenPage
}: UsePagePreloadingProps): UsePagePreloadingResult => {
  // Keep track of currently visible pages
  const [visibleLeftPage, setVisibleLeftPage] = useState<Page | null>(null);
  const [visibleRightPage, setVisibleRightPage] = useState<Page | null>(null);
  const [visibleFullscreenPage, setVisibleFullscreenPage] = useState<Page | null>(null);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Set of page IDs that have been preloaded
  const [preloadedPages, setPreloadedPages] = useState<Set<string>>(new Set());

  // Function to mark a page as preloaded
  const markPageAsPreloaded = (page: Page | null) => {
    if (!page) return;
    
    setPreloadedPages(prev => {
      const newSet = new Set(prev);
      newSet.add(page.id);
      return newSet;
    });
  };

  // Check if a page is preloaded
  const isPagePreloaded = (page: Page | null): boolean => {
    if (!page) return true; // No page is considered "preloaded"
    return preloadedPages.has(page.id);
  };

  // Check if current pages are loaded and ready to display
  useEffect(() => {
    // If we're switching to a fullscreen page
    if (fullscreenPage) {
      if (isPagePreloaded(fullscreenPage)) {
        setVisibleFullscreenPage(fullscreenPage);
        setVisibleLeftPage(null);
        setVisibleRightPage(null);
        setIsLoading(false);
      } else {
        setIsLoading(true);
        // Simulate loading time (remove in production and use real loading checks)
        setTimeout(() => {
          markPageAsPreloaded(fullscreenPage);
        }, 50); // Very short timeout for near-immediate loading
      }
    } 
    // If we're displaying left/right pages
    else {
      const leftLoaded = isPagePreloaded(leftPage);
      const rightLoaded = isPagePreloaded(rightPage);
      
      if (leftLoaded && rightLoaded) {
        setVisibleLeftPage(leftPage);
        setVisibleRightPage(rightPage);
        setVisibleFullscreenPage(null);
        setIsLoading(false);
      } else {
        setIsLoading(true);
        // Simulate loading time (remove in production and use real loading checks)
        setTimeout(() => {
          if (!leftLoaded) markPageAsPreloaded(leftPage);
          if (!rightLoaded) markPageAsPreloaded(rightPage);
        }, 50); // Very short timeout for near-immediate loading
      }
    }
  }, [leftPage, rightPage, fullscreenPage]);

  // Preload next pages in the background
  useEffect(() => {
    // Queue the next pages for preloading
    const pagesToPreload = [nextLeftPage, nextRightPage, nextFullscreenPage].filter(Boolean);
    
    // For each page that needs preloading
    pagesToPreload.forEach(page => {
      if (page && !isPagePreloaded(page)) {
        // Simulate preloading (in a real app, you would preload content here)
        setTimeout(() => {
          markPageAsPreloaded(page);
        }, 100);
      }
    });
  }, [nextLeftPage, nextRightPage, nextFullscreenPage]);

  return {
    visibleLeftPage,
    visibleRightPage,
    visibleFullscreenPage,
    isLoading
  };
}; 